"use client"

import { useState } from "react"
import { Loader2, ExternalLink } from "lucide-react"
import { useWallet } from "@/lib/wallet-provider"
import { heroTypes } from "@/data/hero-types"
import { levels } from "@/data/levels"
import { formatPlayTime, usePlayerStats } from "@/lib/player-stats"
import { useOnChainScores } from "@/hooks/use-onchain-scores"
import { useLeaderboard } from "@/hooks/use-leaderboard"
import { IdentitySigil } from "@/components/ui/identity-sigil"
import { ChartIcon, TrophyIcon } from "@/components/icons/pixel-icons"
import {
  MenuDetailHeading,
  MenuEmptyState,
  MenuGroup,
  MenuNotice,
  MenuStatRow,
  MenuValueRow,
} from "../menu-primitives"

const truncateKey = (key: string) => `${key.slice(0, 6)}…${key.slice(-4)}`

const levelLabel = (index: number) =>
  levels[index]?.name ? `${levels[index].name} (${index + 1})` : `Level ${index + 1}`

const heroLabel = (id: string) => heroTypes[id]?.name ?? id

/** Personal records held on this device only. */
export const LocalRecordsSection = () => {
  const stats = usePlayerStats()
  const heroEntries = Object.entries(stats.heroes).sort((a, b) => b[1].runs - a[1].runs)

  return (
    <div>
      <MenuDetailHeading
        title="This device"
        blurb="Kept in this browser's storage — no wallet needed, and it never leaves your machine."
      />

      {stats.runs === 0 ? (
        <MenuGroup title="Overall" icon={<ChartIcon size={14} />}>
          <MenuEmptyState>No runs recorded yet. Finish a run to start your record.</MenuEmptyState>
        </MenuGroup>
      ) : (
        <>
          <MenuGroup title="Overall" icon={<ChartIcon size={14} />}>
            <MenuStatRow label="Best score" value={stats.bestScore.toLocaleString()} />
            <MenuStatRow label="Runs played" value={stats.runs.toLocaleString()} />
            <MenuStatRow label="Runs won" value={stats.wins.toLocaleString()} />
            <MenuStatRow label="Deepest level" value={levelLabel(stats.bestLevelIndex)} />
            <MenuStatRow label="Time played" value={formatPlayTime(stats.totalPlayMs)} />
            {stats.lastPlayedAt && (
              <MenuStatRow
                label="Last played"
                value={new Date(stats.lastPlayedAt).toLocaleDateString()}
              />
            )}
          </MenuGroup>

          <MenuGroup title="By hero">
            {heroEntries.length === 0 ? (
              <MenuEmptyState>No hero records yet</MenuEmptyState>
            ) : (
              heroEntries.map(([id, hero]) => (
                <MenuValueRow
                  key={id}
                  label={heroLabel(id)}
                  sublabel={`${hero.runs} run${hero.runs === 1 ? "" : "s"} · ${hero.wins} won · deepest ${levelLabel(hero.bestLevelIndex)}`}
                >
                  <span className="text-sm font-bold text-amber-300 tabular-nums">
                    {hero.bestScore.toLocaleString()}
                  </span>
                </MenuValueRow>
              ))
            )}
          </MenuGroup>
        </>
      )}
    </div>
  )
}

/** The shared, wallet-keyed leaderboard. */
export const GlobalRecordsSection = () => {
  const { entries, isLoading, error, myRank, identityKey } = useLeaderboard()

  return (
    <div>
      <MenuDetailHeading
        title="Global ladder"
        blurb="Best score per wallet identity, submitted automatically at the end of a run."
      />

      {myRank > 0 && (
        <MenuGroup title="You" icon={<TrophyIcon size={14} />}>
          <MenuStatRow label="Your rank" value={`#${myRank} of ${entries.length}`} />
        </MenuGroup>
      )}

      <MenuGroup title="Standings" icon={<TrophyIcon size={14} />}>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 size={20} className="animate-spin text-amber-400" />
          </div>
        ) : error ? (
          <div className="px-4 py-3">
            <MenuNotice tone="error">{error}</MenuNotice>
          </div>
        ) : entries.length === 0 ? (
          <MenuEmptyState>No scores submitted yet</MenuEmptyState>
        ) : (
          entries.map((entry, index) => {
            const isMe = entry.pubkey === identityKey
            return (
              <div
                key={entry.pubkey}
                className={`flex items-center gap-3 px-4 py-2.5 ${isMe ? "bg-amber-900/20" : ""}`}
              >
                <span
                  className={`w-7 shrink-0 text-sm font-bold tabular-nums ${
                    index === 0
                      ? "text-amber-400"
                      : index === 1
                        ? "text-gray-300"
                        : index === 2
                          ? "text-orange-400"
                          : "text-gray-500"
                  }`}
                >
                  {index + 1}
                </span>
                <IdentitySigil value={entry.pubkey} size={22} className="rounded shrink-0" />
                <span className="min-w-0 flex-1 text-xs font-mono text-gray-300 truncate">
                  {truncateKey(entry.pubkey)}
                  {isMe && <span className="ml-2 text-amber-300 font-sans font-bold">you</span>}
                </span>
                <span className="text-sm font-bold text-gray-100 tabular-nums shrink-0">
                  {entry.bestScore.toLocaleString()}
                </span>
              </div>
            )
          })
        )}
      </MenuGroup>
    </div>
  )
}

/**
 * Personal bests published to the BSV blockchain.
 *
 * Publishing writes one 0-satoshi OP_RETURN output from the player's own
 * wallet, so the record is theirs and independently verifiable - the txid links
 * straight to a block explorer. The server re-reads every transaction off the
 * chain before listing it, so nothing here is a claim we merely took on trust.
 */
export const OnChainRecordsSection = () => {
  const { status, identityKey, connect } = useWallet()
  const stats = usePlayerStats()
  const { records, isLoading, isInscribing, inscribe } = useOnChainScores()
  const [message, setMessage] = useState<{ tone: "error" | "success" | "info"; text: string } | null>(
    null,
  )

  const isConnected = status === "connected" && !!identityKey
  const bestPublished = records.length > 0 ? records[0].score : 0
  const canPublish = stats.bestScore > 0 && stats.bestScore > bestPublished

  // Which hero set the local best - published alongside the score so the record
  // says how the run was played, not just what it scored.
  const bestHero =
    Object.entries(stats.heroes).sort((a, b) => b[1].bestScore - a[1].bestScore)[0]?.[0] ?? "unknown"

  const handlePublish = async () => {
    setMessage(null)
    const result = await inscribe({
      score: stats.bestScore,
      heroType: bestHero,
      levelIndex: stats.bestLevelIndex,
    })
    if (result.ok && !result.error) {
      setMessage({ tone: "success", text: `Published on-chain — ${result.txid?.slice(0, 12)}…` })
    } else if (result.ok && result.error) {
      setMessage({ tone: "info", text: result.error })
    } else {
      setMessage({ tone: "error", text: result.error ?? "Could not publish" })
    }
  }

  return (
    <div>
      <MenuDetailHeading
        title="On-chain records"
        blurb="Publish a personal best to the BSV blockchain as a permanent, verifiable record signed by your own wallet."
      />

      {!isConnected ? (
        <MenuGroup title="Wallet">
          <MenuValueRow
            label="Not connected"
            sublabel="Publishing writes a transaction from your own wallet"
          >
            <button
              type="button"
              onClick={() => connect()}
              className="rounded-full border border-amber-600/50 px-3 py-1.5 text-xs font-bold text-amber-200 hover:bg-amber-900/30 transition"
            >
              Connect
            </button>
          </MenuValueRow>
        </MenuGroup>
      ) : (
        <MenuGroup title="Publish" icon={<TrophyIcon size={14} />}>
          <MenuValueRow
            label="Local best"
            sublabel={
              stats.bestScore === 0
                ? "Finish a run first"
                : `${heroLabel(bestHero)} · ${levelLabel(stats.bestLevelIndex)}`
            }
          >
            <span className="text-sm font-bold text-amber-300 tabular-nums">
              {stats.bestScore.toLocaleString()}
            </span>
          </MenuValueRow>
          <MenuValueRow
            label="Published best"
            sublabel={
              bestPublished === 0
                ? "Nothing published yet"
                : canPublish
                  ? "Your local best beats it"
                  : "Up to date"
            }
          >
            <span className="text-sm font-bold text-gray-100 tabular-nums">
              {bestPublished === 0 ? "—" : bestPublished.toLocaleString()}
            </span>
          </MenuValueRow>
          <div className="px-4 py-3">
            <button
              type="button"
              onClick={handlePublish}
              disabled={!canPublish || isInscribing}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-amber-700 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm py-2.5 transition"
            >
              {isInscribing ? <Loader2 size={16} className="animate-spin" /> : null}
              {isInscribing ? "Waiting for your wallet…" : "Publish best on-chain"}
            </button>
            <p className="mt-2 text-[11px] text-gray-500">
              Your wallet covers the mining fee (a few satoshis) and will ask you to approve the
              transaction.
            </p>
            {message && <MenuNotice tone={message.tone}>{message.text}</MenuNotice>}
          </div>
        </MenuGroup>
      )}

      <MenuGroup title="Published records">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 size={20} className="animate-spin text-amber-400" />
          </div>
        ) : records.length === 0 ? (
          <MenuEmptyState>
            {isConnected ? "No records published yet" : "Connect a wallet to see your records"}
          </MenuEmptyState>
        ) : (
          records.map((record) => (
            <a
              key={record.txid}
              href={`https://whatsonchain.com/tx/${record.txid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-gray-100 tabular-nums">
                  {record.score.toLocaleString()}
                </span>
                <span className="block text-xs text-gray-400 mt-0.5 truncate">
                  {heroLabel(record.heroType)} · {levelLabel(record.levelIndex)} ·{" "}
                  {new Date(record.inscribedAt).toLocaleDateString()}
                </span>
              </span>
              <span className="text-xs font-mono text-gray-500 shrink-0">
                {record.txid.slice(0, 8)}…
              </span>
              <ExternalLink size={14} className="text-gray-500 shrink-0" />
            </a>
          ))
        )}
      </MenuGroup>
    </div>
  )
}
